import React, { useState, useEffect, useCallback } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { Card, CardTitle, Insight, Btn } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
// 25 Oct 2026: admin-side proxy (api/geocode-lazy.js) checks the admin session and
// forwards to sanoire-api. Before this the call hit the SPA rewrite and did nothing.
const GEOCODE_LAZY_URL = '/api/geocode-lazy';

const _COUNTRIES_PRIORITY = [
  'Cambodia','Thailand','Malaysia','Singapore','Vietnam','Indonesia',
  'Philippines','Japan','South Korea','Taiwan','Hong Kong','Myanmar',
  'Laos','Bangladesh','Sri Lanka','Nepal','India',
  'United Arab Emirates','Saudi Arabia','Qatar','Jordan','Lebanon',
  'Kenya','Nigeria','Ghana','Egypt','Morocco','South Africa',
  'France','Germany','United Kingdom','Spain','Italy',
  'Australia','New Zealand','United States','Canada',
];

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

function GeocodingBody() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const isMobile = useIsMobile();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null); // country being geocoded
  const [results, setResults] = useState({});   // { country: { geocoded, failed } }
  const [batchLimit, setBatchLimit] = useState(20);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      // True counts via count=exact (HEAD requests — no row cap)
      const [
        { count: total },
        { count: geocoded },
        { count: missing },
        { data: countryRows, error: rpcError },
      ] = await Promise.all([
        supabase.from('Locations').select('*', { count: 'exact', head: true }),
        supabase.from('Locations').select('*', { count: 'exact', head: true }).not('latitude', 'is', null),
        supabase.from('Locations').select('*', { count: 'exact', head: true }).is('latitude', null),
        supabase.rpc('get_ungeocoded_by_country'),
      ]);
      if (rpcError) throw rpcError;

      const rows = (countryRows || []).map(r => ({ country: r.country, ungeocoded: Number(r.ungeocoded) }));
      setStats({ rows, total: total || 0, geocoded: geocoded || 0, ungeocoded: missing || 0 });
    } catch (_e) {
      setStats(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  async function triggerGeocode(country) {
    setRunning(country);
    try {
      const res = await fetch(GEOCODE_LAZY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + ((await supabase.auth.getSession()).data?.session?.access_token || '') },
        body: JSON.stringify({ country, limit: batchLimit }),
      });
      const data = await res.json();
      setResults(prev => ({ ...prev, [country]: data }));
      await fetchStats();
    } catch (_e) {
      setResults(prev => ({ ...prev, [country]: { error: 'Request failed' } }));
    }
    setRunning(null);
  }

  const pct = stats ? Math.round((stats.geocoded / stats.total) * 100) : 0;

  return (
    <div style={{ fontFamily: F, padding: isMobile ? '16px 14px 80px' : '24px 28px' }}>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 500, margin: 0, color: 'var(--color-text-primary)' }}>
          Geocoding
        </h2>
        <p style={{ fontSize: '.78rem', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
          Manage GPS coordinate coverage for near-me search. Rows without coordinates return L3 (zero results) on location queries.
        </p>
      </div>

      {/* Coverage summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total locations', value: loading ? '…' : stats?.total?.toLocaleString() || '0', accent: '#314F5C' },
          { label: 'Geocoded', value: loading ? '…' : stats?.geocoded?.toLocaleString() || '0', accent: '#28a745' },
          { label: 'Missing coords', value: loading ? '…' : stats?.ungeocoded?.toLocaleString() || '0', accent: '#dc3545' },
          { label: 'Coverage', value: loading ? '…' : pct + '%', accent: pct > 80 ? '#28a745' : pct > 50 ? '#fd7e14' : '#dc3545' },
        ].map(({ label, value, accent }) => (
          <Card key={label} style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: '.68rem', color: 'var(--color-text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 500, color: accent }}>{value}</div>
          </Card>
        ))}
      </div>

      {/* Coverage bar */}
      {!loading && stats && (
        <Card style={{ marginBottom: 20, padding: '14px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.72rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
            <span>Geocoding coverage</span><span>{pct}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: 'var(--color-background-tertiary)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: pct + '%', background: pct > 80 ? '#28a745' : pct > 50 ? '#fd7e14' : '#dc3545', borderRadius: 3, transition: 'width .4s' }} />
          </div>
        </Card>
      )}

      {/* Batch size picker + refresh */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <span style={{ fontSize: '.78rem', color: 'var(--color-text-secondary)' }}>Batch size (cloud max 40):</span>
        {[20, 40].map(n => (
          <button key={n} onClick={() => setBatchLimit(n)} style={{
            padding: '4px 12px', borderRadius: 6, border: '1px solid var(--color-border-secondary)',
            background: batchLimit === n ? '#314F5C' : 'transparent',
            color: batchLimit === n ? '#AFE1F6' : 'var(--color-text-secondary)',
            fontSize: '.75rem', cursor: 'pointer', fontFamily: F,
          }}>{n} rows</button>
        ))}
        <button onClick={fetchStats} style={{
          marginLeft: 'auto', padding: '4px 12px', borderRadius: 6,
          border: '1px solid var(--color-border-secondary)', background: 'transparent',
          color: 'var(--color-text-secondary)', fontSize: '.75rem', cursor: 'pointer', fontFamily: F,
        }}>↻ Refresh</button>
      </div>

      {/* Country table */}
      <Card style={{ marginBottom: 24 }}>
        <CardTitle>Ungeocoded rows by country</CardTitle>
        {loading ? (
          <div style={{ fontSize: '.78rem', color: 'var(--color-text-secondary)', padding: '8px 0' }}>Loading…</div>
        ) : !stats?.rows?.length ? (
          <Insight type="ok">All rows are geocoded.</Insight>
        ) : (
          <div>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 120px 100px', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--color-border-tertiary)', fontSize: '.65rem', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              <div>Country</div><div style={{ textAlign: 'right' }}>Missing</div><div style={{ textAlign: 'center' }}>Cloud geocode</div><div style={{ textAlign: 'center' }}>Result</div>
            </div>
            {stats.rows.map(({ country, ungeocoded }) => {
              const res = results[country];
              const isRunning = running === country;
              return (
                <div key={country} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 120px 100px', gap: 8, padding: '7px 0', borderBottom: '.5px solid var(--color-border-tertiary)', alignItems: 'center' }}>
                  <div style={{ fontSize: '.78rem', color: 'var(--color-text-primary)', fontWeight: 500 }}>{country}</div>
                  <div style={{ fontSize: '.78rem', color: ungeocoded > 100 ? '#dc3545' : ungeocoded > 20 ? '#fd7e14' : '#28a745', textAlign: 'right', fontWeight: 500 }}>{ungeocoded.toLocaleString()}</div>
                  <div style={{ textAlign: 'center' }}>
                    <button
                      onClick={() => triggerGeocode(country)}
                      disabled={!!running}
                      style={{
                        padding: '4px 10px', borderRadius: 6, border: '1px solid var(--color-border-secondary)',
                        background: isRunning ? '#314F5C' : 'transparent',
                        color: isRunning ? '#AFE1F6' : 'var(--color-text-secondary)',
                        fontSize: '.72rem', cursor: running ? 'not-allowed' : 'pointer', fontFamily: F,
                        opacity: running && !isRunning ? 0.4 : 1,
                      }}
                    >{isRunning ? `Geocoding…` : `▶ Run ${batchLimit}`}</button>
                  </div>
                  <div style={{ fontSize: '.72rem', textAlign: 'center', color: res?.error ? '#dc3545' : res ? '#28a745' : 'var(--color-text-secondary)' }}>
                    {res?.error ? '✗ Error' : res ? `✓ ${res.geocoded} done` : '—'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Terminal command generator */}
      <Card>
        <CardTitle>Bulk geocode — terminal commands</CardTitle>
        <p style={{ fontSize: '.75rem', color: 'var(--color-text-secondary)', margin: '0 0 14px' }}>
          For large runs (500+ rows), the cloud geocoder will timeout. Run these commands locally in your terminal. Resumable — safe to stop and restart anytime.
        </p>
        <Insight type="info">Requires Node.js + Google Maps API key in .env at <code>{process.env.REACT_APP_SYNC_DIR || 'sanoire-sync-new'}</code></Insight>
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(stats?.rows?.slice(0, 8) || []).map(({ country, ungeocoded }) => {
            const syncDir = process.env.REACT_APP_SYNC_DIR || 'sanoire-sync-new';
            const cmd = `node scripts/geocode.js --country="${country}" --continuous`;
            return (
              <div key={country} style={{ background: 'var(--color-background-tertiary)', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: '.72rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>{country} — {ungeocoded.toLocaleString()} rows</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(`cd ${syncDir}\n${cmd}`)}
                    style={{ fontSize: '.65rem', color: '#6F8FA7', background: 'none', border: '1px solid var(--color-border-secondary)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontFamily: F }}
                  >Copy</button>
                </div>
                <code style={{ fontSize: '.7rem', color: '#AFE1F6', background: '#0d1d2b', borderRadius: 6, padding: '6px 10px', display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' }}>
                  {`cd ${syncDir}`}
                  <br />
                  {cmd}
                </code>
              </div>
            );
          })}
          {/* Generic all-countries command */}
          <div style={{ background: 'var(--color-background-tertiary)', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: '.72rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>All countries (full run)</span>
              <button
                onClick={() => navigator.clipboard.writeText(`cd ${process.env.REACT_APP_SYNC_DIR || 'sanoire-sync-new'}\nnode scripts/geocode.js --continuous`)}
                style={{ fontSize: '.65rem', color: '#6F8FA7', background: 'none', border: '1px solid var(--color-border-secondary)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontFamily: F }}
              >Copy</button>
            </div>
            <code style={{ fontSize: '.7rem', color: '#AFE1F6', background: '#0d1d2b', borderRadius: 6, padding: '6px 10px', display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' }}>
              cd {process.env.REACT_APP_SYNC_DIR || 'sanoire-sync-new'}
              <br />
              node scripts/geocode.js --continuous
            </code>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function Geocoding(props) {
  if (props && props.embed) return <GeocodingBody {...props} />;
  return <ConsoleShell title="Geocoding"><GeocodingBody {...props} /></ConsoleShell>;
}
